import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';

import { superadminService } from '../../../services/superadmin.service';
import SalonAddressPicker from '../../pro/salon/SalonAddressPicker';
import LocationStatusChip from './LocationStatusChip';

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const distanceInKm = (from, to) => {
  const fromLatitude = toNumber(from?.latitude);
  const fromLongitude = toNumber(from?.longitude);
  const toLatitude = toNumber(to?.latitude);
  const toLongitude = toNumber(to?.longitude);
  if (
    fromLatitude === null
    || fromLongitude === null
    || toLatitude === null
    || toLongitude === null
  ) {
    return null;
  }

  const radians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371.0088;
  const latitudeDelta = radians(toLatitude - fromLatitude);
  const longitudeDelta = radians(toLongitude - fromLongitude);
  const a = (
    Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(radians(fromLatitude))
      * Math.cos(radians(toLatitude))
      * Math.sin(longitudeDelta / 2) ** 2
  );
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const candidateFromLocation = (location) => {
  if (!location) return null;
  return {
    formatted_address: location.formatted_address || '',
    latitude: location.latitude,
    longitude: location.longitude,
    country_code: location.country_code || '',
    country_name: location.country_name || '',
    admin_area_1: location.admin_area_1 || '',
    admin_area_2: location.admin_area_2 || '',
    locality: location.locality || '',
    postal_code: location.postal_code || '',
    provider: location.provider || location.geocoding_provider || '',
    provider_place_id: location.provider_place_id || '',
    raw: location.raw || location.address_components || {},
  };
};

const LocationValues = ({ title, location }) => (
  <Box sx={{ flex: 1, minWidth: 240 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
      {title}
    </Typography>
    {location ? (
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {location.formatted_address || 'Adresse absente'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Wilaya : {location.admin_area_1 || '—'} · Commune : {location.locality || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Code postal : {location.postal_code || '—'} · Pays : {location.country_code || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Coordonnées : {location.latitude ?? '—'}, {location.longitude ?? '—'}
        </Typography>
      </Stack>
    ) : (
      <Typography variant="body2" color="error">
        Aucune localisation enregistrée
      </Typography>
    )}
  </Box>
);

const SalonLocationRepairPanel = ({ salon, onUpdated, onOpenHistory }) => {
  const initialCandidate = salon?.latest_location_repair?.candidate || null;
  const [candidate, setCandidate] = useState(initialCandidate);
  const [job, setJob] = useState(salon?.latest_location_repair || null);
  const [reason, setReason] = useState('');
  const [riskConfirmed, setRiskConfirmed] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setCandidate(salon?.latest_location_repair?.candidate || null);
    setJob(salon?.latest_location_repair || null);
    setRiskConfirmed(false);
    setMessage(null);
  }, [salon]);

  const currentLocation = salon?.location || null;
  const distance = useMemo(
    () => distanceInKm(currentLocation, candidate),
    [candidate, currentLocation],
  );
  const countryChanged = Boolean(
    currentLocation?.country_code
    && candidate?.country_code
    && currentLocation.country_code.toUpperCase()
      !== candidate.country_code.toUpperCase(),
  );
  const isDistant = distance !== null && distance > 2;
  const requiresExplicitConfirmation = isDistant || countryChanged;

  const retry = async (forceRefresh) => {
    setBusyAction(forceRefresh ? 'force' : 'retry');
    setMessage(null);
    try {
      const response = await superadminService.retrySalonLocation(
        salon.salon_id,
        { force_refresh: forceRefresh },
      );
      const nextJob = response?.data?.job || null;
      setJob(nextJob);
      if (nextJob?.candidate && Object.keys(nextJob.candidate).length > 0) {
        setCandidate(nextJob.candidate);
      }
      setMessage({
        severity: 'success',
        text: nextJob?.candidate
          ? 'Nouvelle proposition reçue. Vérifiez-la avant de l’appliquer.'
          : 'Réparation mise en file. Actualisez dans quelques instants.',
      });
      onUpdated?.();
    } catch (error) {
      setMessage({
        severity: 'error',
        text: error?.response?.data?.detail || 'Impossible de relancer la localisation.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const applyCandidate = async () => {
    if (!candidate) {
      setMessage({
        severity: 'error',
        text: 'Sélectionnez ou récupérez d’abord une adresse complète.',
      });
      return;
    }
    if (!reason.trim()) {
      setMessage({
        severity: 'error',
        text: 'Le motif de la correction est obligatoire.',
      });
      return;
    }
    if (requiresExplicitConfirmation && !riskConfirmed) {
      setMessage({
        severity: 'warning',
        text: 'Confirmez explicitement cet écart avant d’appliquer la correction.',
      });
      return;
    }

    setBusyAction('apply');
    setMessage(null);
    try {
      const response = await superadminService.applySalonLocation(
        salon.salon_id,
        {
          candidate,
          reason: reason.trim(),
        },
      );
      setMessage({
        severity: 'success',
        text: 'Localisation corrigée et enregistrée.',
      });
      onUpdated?.(response.data);
    } catch (error) {
      const details = error?.response?.data;
      setMessage({
        severity: 'error',
        text: details?.reason
          || details?.detail
          || 'Impossible d’appliquer cette correction.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const missingFields = salon?.location_missing_fields || [];

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Qualité de la localisation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vérifiez, relancez ou corrigez les données utilisées par la recherche.
            </Typography>
          </Box>
          <LocationStatusChip status={salon?.location_status} />
        </Stack>

        {missingFields.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Champs manquants : {missingFields.join(', ')}
          </Alert>
        )}

        {currentLocation?.last_geocoding_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Dernière erreur : {currentLocation.last_geocoding_error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            type="button"
            variant="outlined"
            startIcon={busyAction === 'retry' ? <CircularProgress size={16} /> : <AutorenewIcon />}
            onClick={() => retry(false)}
            disabled={Boolean(busyAction)}
          >
            Relancer
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="warning"
            startIcon={busyAction === 'force' ? <CircularProgress size={16} /> : <PublishedWithChangesIcon />}
            onClick={() => retry(true)}
            disabled={Boolean(busyAction)}
          >
            Forcer le géocodage
          </Button>
          {job?.status && (
            <LocationStatusChip status={job.status} />
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <LocationValues title="Valeurs actuelles" location={currentLocation} />
          <LocationValues title="Proposition" location={candidate} />
        </Stack>

        {isDistant && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Écart supérieur à 2 km : environ {distance.toFixed(1)} km.
          </Alert>
        )}
        {countryChanged && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Le pays proposé est différent du pays actuellement enregistré.
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Correction manuelle
          </Typography>
          <SalonAddressPicker
            value={candidate ? candidateFromLocation(candidate) : null}
            onChange={(nextCandidate) => {
              setCandidate(nextCandidate);
              setRiskConfirmed(false);
              setMessage(null);
            }}
            requireConfirmation
            disabled={Boolean(busyAction)}
          />
        </Box>

        <TextField
          fullWidth
          label="Motif de la correction"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          margin="normal"
          required
          disabled={Boolean(busyAction)}
        />

        {requiresExplicitConfirmation && (
          <FormControlLabel
            control={(
              <Checkbox
                checked={riskConfirmed}
                onChange={(event) => setRiskConfirmed(event.target.checked)}
              />
            )}
            label="Je confirme cet écart de localisation"
          />
        )}

        {message && (
          <Alert severity={message.severity} sx={{ my: 2 }}>
            {message.text}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            type="button"
            variant="contained"
            startIcon={busyAction === 'apply' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={applyCandidate}
            disabled={Boolean(busyAction) || !candidate}
          >
            Appliquer la correction
          </Button>
          {onOpenHistory && (
            <Button
              type="button"
              variant="text"
              startIcon={<HistoryIcon />}
              onClick={onOpenHistory}
            >
              Voir l’historique
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SalonLocationRepairPanel;
